import { supabase } from '../lib/supabase';
import { HeroBanner } from '../types';

export const bannerService = {
    async getAll(): Promise<HeroBanner[]> {
        const { data, error } = await supabase
            .from('hero_banners')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true });

        if (error) {
            console.error('Error fetching banners:', error);
            return [];
        }

        return data.map((b: any) => ({
            id: b.id,
            title: b.title,
            subtitle: b.subtitle,
            image: b.image_url,
            position: b.position,
            scale: b.scale || 1.0,
            active: b.is_active,
            order: b.display_order
        }));
    },

    async getAllAdmin(): Promise<HeroBanner[]> {
        const { data, error } = await supabase
            .from('hero_banners')
            .select('*')
            .order('display_order', { ascending: true });

        if (error) {
            console.error('Error fetching banners for admin:', error);
            return [];
        }

        return data.map((b: any) => ({
            id: b.id,
            title: b.title,
            subtitle: b.subtitle,
            image: b.image_url,
            position: b.position,
            scale: b.scale || 1.0,
            active: b.is_active,
            order: b.display_order
        }));
    },

    async create(banner: Omit<HeroBanner, 'id'>): Promise<HeroBanner | null> {
        const { data, error } = await supabase
            .from('hero_banners')
            .insert([{
                title: banner.title,
                subtitle: banner.subtitle,
                image_url: banner.image,
                position: banner.position,
                scale: banner.scale || 1.0,
                is_active: banner.active,
                display_order: banner.order
            }])
            .select()
            .single();

        if (error) {
            console.error('Error creating banner:', error);
            return null;
        }

        return {
            id: data.id,
            title: data.title,
            subtitle: data.subtitle,
            image: data.image_url,
            position: data.position,
            scale: data.scale,
            active: data.is_active,
            order: data.display_order
        };
    },

    async update(id: string, banner: Partial<HeroBanner>): Promise<HeroBanner | null> {
        const updates: any = {};
        if (banner.title !== undefined) updates.title = banner.title;
        if (banner.subtitle !== undefined) updates.subtitle = banner.subtitle;
        if (banner.image !== undefined) updates.image_url = banner.image;
        if (banner.position !== undefined) updates.position = banner.position;
        if (banner.scale !== undefined) updates.scale = banner.scale;
        if (banner.active !== undefined) updates.is_active = banner.active;
        if (banner.order !== undefined) updates.display_order = banner.order;

        const { data, error } = await supabase
            .from('hero_banners')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            console.error('Error updating banner:', error);
            return null;
        }

        return {
            id: data.id,
            title: data.title,
            subtitle: data.subtitle,
            image: data.image_url,
            position: data.position,
            scale: data.scale,
            active: data.is_active,
            order: data.display_order
        };
    },

    async delete(id: string): Promise<boolean> {
        const { error } = await supabase
            .from('hero_banners')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting banner:', error);
            return false;
        }
        return true;
    }
};
